/**
 * Action Executor
 *
 * Executes declarative step actions defined in tutorial configuration.
 * This converts JSON action definitions into actual DOM manipulations.
 */

import { Information } from "../../simulation/types"
import { StepAction } from "../types/tutorial-config"
import { resolveSelector, resolveTreeNodeId } from "./tree-resolver"

/**
 * Executes a single step action immediately (no delay handling).
 * Internal function - use executeAction for delay support.
 */
function executeActionImmediate(
  action: StepAction,
  selectorMap: Record<string, string>,
  categories: Information[]
): void {
  // Actions that don't need a target element
  if (action.action === "clearSearch") {
    const clearButton = document.getElementById("clearTreeSearchButton")
    if (clearButton) {
      clearButton.click()
    }
    return
  }

  // Tree path actions use treePath instead of target
  if (action.action === "expandTreePath" || action.action === "selectTreeNode") {
    executeTreePathAction(action, categories)
    return
  }

  const target = resolveSelector(action.target, selectorMap)
  if (!target) {
    console.warn(`[TutorialAction] No target specified for action: ${action.action}`)
    return
  }

  const element = document.querySelector(target) as HTMLElement | null

  if (!element) {
    console.warn(`[TutorialAction] Element not found: ${target}`)
    return
  }

  switch (action.action) {
    case "hideElement":
      element.style.setProperty("display", "none")
      break

    case "showElement":
      element.style.setProperty("display", action.value || "flex")
      break

    case "setStyle":
      if (action.value) {
        // value format: "property:value" (e.g., "pointer-events:none")
        const [property, value] = action.value.split(":")
        if (property && value) {
          element.style.setProperty(property.trim(), value.trim())
        }
      }
      break

    case "addClass":
      if (action.value) {
        element.classList.add(action.value)
      }
      break

    case "removeClass":
      if (action.value) {
        element.classList.remove(action.value)
      }
      break

    case "click":
      element.click()
      break

    case "setPointerEvents":
      element.style.setProperty("pointer-events", action.value || "auto")
      break

    case "scrollIntoView":
      element.scrollIntoView({ behavior: "smooth", block: "nearest" })
      break

    case "disableButton":
      if (element instanceof HTMLButtonElement) {
        element.disabled = true
      }
      break

    case "enableButton":
      if (element instanceof HTMLButtonElement) {
        element.disabled = false
      }
      break

    case "setValue":
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        // React tracks input values internally; assigning .value directly does
        // not trigger onChange. Use the native setter and dispatch an input
        // event so controlled components update their state.
        const proto =
          element instanceof HTMLInputElement
            ? HTMLInputElement.prototype
            : HTMLTextAreaElement.prototype
        const nativeSetter = Object.getOwnPropertyDescriptor(proto, "value")?.set
        if (nativeSetter) {
          nativeSetter.call(element, action.value || "")
        } else {
          element.value = action.value || ""
        }
        element.dispatchEvent(new Event("input", { bubbles: true }))
      }
      break

    case "expandNode":
      // Click the expand button within the target element (typically a tree-item-container)
      // Uses tree-switcher-closed.svg for collapsed nodes
      {
        const expandButton = element.querySelector(
          "img[src*='tree-switcher-closed'], img[alt='expand']"
        ) as HTMLElement
        if (expandButton) {
          expandButton.click()
        }
      }
      break

    default:
      console.warn(`[TutorialAction] Unknown action type: ${action.action}`)
  }
}

/**
 * Waits for an element with the given id to appear in the DOM.
 */
function waitForElementById(
  id: string,
  timeout: number = 2000
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const startTime = Date.now()
    const check = (): void => {
      const el = document.getElementById(id)
      if (el) {
        resolve(el)
        return
      }
      if (Date.now() - startTime < timeout) {
        setTimeout(check, 50)
      } else {
        resolve(null)
      }
    }
    check()
  })
}

/**
 * Returns true if a node container appears expanded (its children container
 * exists in the DOM, regardless of how the tree library encodes the state).
 */
function isNodeExpanded(nodeId: number): boolean {
  // The children wrapper is created when a tree branch is expanded.
  // We look for any tree-item descendants under the node container.
  const container = document.getElementById(`tree-item-${nodeId}-container`)
  if (!container) return false
  return container.querySelector('[id^="tree-item-"]:not([id$="-container"])') !== null
}

/**
 * Finds the expand chevron for a specific tree node. Restricts the search to
 * direct chevron elements of the node's container, never a descendant node's
 * chevron.
 */
function findExpandChevron(nodeId: number): HTMLElement | null {
  const container = document.getElementById(`tree-item-${nodeId}-container`)
  if (!container) return null
  // Only consider images that are not nested inside another tree item.
  const imgs = Array.from(
    container.querySelectorAll<HTMLImageElement>(
      "img[src*='tree-switcher-closed'], img[alt='expand']"
    )
  )
  for (const img of imgs) {
    const closestItem = img.closest('[id^="tree-item-"]:not([id$="-container"])')
    // The chevron belongs to this node only if walking up does not pass
    // through a different tree item.
    if (!closestItem || closestItem.id === `tree-item-${nodeId}`) {
      return img
    }
  }
  return null
}

/**
 * Executes tree path actions (expandTreePath, selectTreeNode).
 *
 * Uses the data-driven resolveTreeNodeId from the simulation's Information
 * tree to find stable node IDs, then operates on `#tree-item-{id}` elements
 * directly. This avoids fragile DOM text matching that breaks under search
 * filtering or accumulated subtree text content.
 */
async function executeTreePathAction(
  action: StepAction,
  categories: Information[]
): Promise<void> {
  if (!action.treePath || action.treePath.length === 0) {
    console.warn(`[TutorialAction] No treePath specified for ${action.action}`)
    return
  }

  for (let i = 0; i < action.treePath.length; i++) {
    const prefix = action.treePath.slice(0, i + 1)
    const nodeId = resolveTreeNodeId(categories, prefix)
    if (nodeId === null) {
      console.warn(
        `[TutorialAction] Could not resolve path: ${prefix.join(" > ")}`
      )
      return
    }

    const isLastSegment = i === action.treePath.length - 1

    // Make sure the node element exists before we try to interact with it.
    const node = await waitForElementById(`tree-item-${nodeId}`)
    if (!node) {
      console.warn(`[TutorialAction] Tree node not found in DOM: tree-item-${nodeId}`)
      return
    }

    if (isLastSegment && action.action === "selectTreeNode") {
      node.click()
      return
    }

    if (!isLastSegment || action.action === "expandTreePath") {
      if (!isNodeExpanded(nodeId)) {
        const chevron = findExpandChevron(nodeId)
        if (chevron) {
          chevron.click()
          // Wait briefly for children to render, but bound by a real check.
          await waitForElementById(`tree-item-${nodeId}-container`, 1000)
          await new Promise((resolve) => setTimeout(resolve, 100))
        }
      }
    }
  }
}

/**
 * Executes a single step action.
 *
 * @param action - The action definition to execute
 * @param selectorMap - Map of logical names to resolved selectors
 * @param categories - Simulation Information[] tree (for treePath actions)
 */
export function executeAction(
  action: StepAction,
  selectorMap: Record<string, string>,
  categories: Information[]
): void {
  if (action.delay && action.delay > 0) {
    setTimeout(() => {
      executeActionImmediate(action, selectorMap, categories)
    }, action.delay)
  } else {
    executeActionImmediate(action, selectorMap, categories)
  }
}

/**
 * Executes an array of step actions in sequence.
 */
export function executeActions(
  actions: StepAction[],
  selectorMap: Record<string, string>,
  categories: Information[]
): void {
  for (const action of actions) {
    executeAction(action, selectorMap, categories)
  }
}

/**
 * Creates a function that executes the given actions.
 * Useful for passing to Shepherd's when.show/when.hide callbacks.
 */
export function createActionExecutor(
  actions: StepAction[] | undefined,
  selectorMap: Record<string, string>,
  categories: Information[]
): (() => void) | undefined {
  if (!actions || actions.length === 0) {
    return undefined
  }

  return () => executeActions(actions, selectorMap, categories)
}
